import { Injectable } from '@angular/core';
import { ActivatedRoute, IsActiveMatchOptions, NavigationStart, ParamMap, Router } from '@angular/router';

import * as _ from 'lodash';
import moment from 'moment';
import { shareReplay, tap, map } from 'rxjs/operators';
import { BehaviorSubject, Observable, ReplaySubject, Subscription } from 'rxjs';
import { OpenFeature } from '@openfeature/web-sdk';

import { ListItem } from 'fc-ui-components';
import { ApiModel, S3File, S3FileModel } from 'models';

import { sortContentProductSearchResult } from 'app/content/helpers/sort-content-products';
import { ENABLE_CONTENT_SORT_BY_CATEGORY_FLAG } from 'app/content/constants/content-product-search.constants';
import { FcProductName } from 'types';

import { MainCategoryTranslationAndSorting } from 'app/b2b-order/components/category-list/category-list.component';
import { FacetOptions, FilterOptions } from '../../../b2b-order/retailer-platform/order/types';
import { LanguageService } from '../language.service';
import { OrderProduct } from '../../../b2b-order/services/order-product-detail';
import { SegmentService } from '../segment.service';
import { TrackingEvents } from '../tracking/gtm';
import { FacetsUiMapper } from './facets-ui-mapper';
import { FiltersProvider } from './filters-provider';
import { RouterHandler } from './router-handler';
import { Searcher } from './searcher';
import {
    ArticleForOrder,
    Facet,
    FacetWithName,
    SearchParameters,
    Sorting,
    SortingCriteria,
    isProductSearchResult
} from './searcher.types';

@Injectable()
export class ProductSearchService {
    public categoriesConfiguration: Observable<MainCategoryTranslationAndSorting>;

    private router: Router;
    private routerHandler: RouterHandler;
    private searcher: Searcher;
    private facetsUiMapper: FacetsUiMapper;
    private filtersProvider: FiltersProvider;
    private reorderSearchResult: ReplaySubject<ReorderSearch>;
    private preorderSearchResult: ReplaySubject<any>;
    private reorderSearchSubscription: Subscription;
    private preorderSearchSubscription: Subscription;
    private searchCache: BehaviorSubject<{ value: ReorderSearch; ttl: number }>;
    private latestRouterEvent: NavigationStart;
    private preorderSearchCache: BehaviorSubject<{ value: ReorderSearch; ttl: number }>;
    private route: ActivatedRoute;
    private routerMatchConfig: IsActiveMatchOptions = {
        paths: 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored'
    };

    constructor(
        router: Router,
        languageService: LanguageService,
        apiModel: ApiModel,
        private s3FileModel: S3FileModel,
        private segmentService: SegmentService
    ) {
        router.events.forEach((event) => {
            if (event instanceof NavigationStart) {
                const isLoggingOut = event.url === '/signin';
                if (isLoggingOut) {
                    this.cleanUp();
                }

                this.latestRouterEvent = event;
            }
        });

        this.router = router;
        this.facetsUiMapper = new FacetsUiMapper(languageService);
        this.routerHandler = new RouterHandler(router, this.route);
        this.searcher = new Searcher(apiModel);
        this.filtersProvider = new FiltersProvider(languageService, s3FileModel);
        this.categoriesConfiguration = this.s3FileModel.getJSON(S3File.CategoriesConfiguration).pipe(shareReplay());

        this.reorderSearchResult = new ReplaySubject(1);
        this.preorderSearchResult = new ReplaySubject(1);
        this.searchCache = new BehaviorSubject({ value: null, ttl: null });
        this.preorderSearchCache = new BehaviorSubject({ value: null, ttl: null });
    }

    public contentAreaSearch(searchParameters: SearchParameters, language: string) {
        return this.searcher.contentAreaSearch(searchParameters, language).pipe(
            map((searchResult) => {
                const isSortByCategoryEnabled = OpenFeature.getClient().getBooleanValue(
                    ENABLE_CONTENT_SORT_BY_CATEGORY_FLAG,
                    false
                );

                return isSortByCategoryEnabled ? sortContentProductSearchResult(searchResult) : searchResult;
            }),
            tap((searchResult) => {
                if (isProductSearchResult(searchResult)) {
                    this.onSearchResult(FcProductName.ContentPlatform, searchResult, searchParameters);
                }
            })
        );
    }

    public reorderAreaSearch(
        searchParameters: SearchParameters,
        organizationId: string,
        cacheSettings: { ttl: { [key: string]: number } } = { ttl: { hours: 1 } }
    ) {
        if (!this.isRequestRequired(OrderProduct.Reorder, searchParameters, organizationId)) {
            const { value: cachedContent } = this.searchCache.getValue();
            this.reorderSearchResult.next(cachedContent);
            return;
        }

        if (this.reorderSearchSubscription && !this.reorderSearchSubscription.closed) {
            this.reorderSearchSubscription.unsubscribe();
        }

        this.reorderSearchSubscription = this.searcher
            .reorderAreaSearch(searchParameters, organizationId)
            .subscribe((searchResult) => {
                const { query, filters, pagination } = searchParameters;
                const { articles, count, facets, sortingCriteria, errorRaised } = searchResult;
                const { value: cachedContent, ttl } = this.searchCache.getValue();

                const reorderSearchResult = {
                    query,
                    filters,
                    result: {
                        articles: cachedContent ? [...cachedContent.result.articles, ...articles] : articles,
                        count,
                        facets,
                        offset: pagination.offset,
                        sortingCriteria,
                        errorRaised
                    }
                };

                this.searchCache.next({
                    value: reorderSearchResult,
                    ttl: ttl ? ttl : moment().add(cacheSettings.ttl).unix()
                });

                if (reorderSearchResult.result.offset === 0) {
                    this.scrollContainerToTop(OrderProduct.Reorder);
                }

                this.reorderSearchResult.next(reorderSearchResult);
                this.onSearchResult(FcProductName.OrderPlatform, searchResult, searchParameters);
                this.trackReorderEvents(reorderSearchResult);
            });
    }

    public getReorderSearchResult() {
        return this.reorderSearchResult.pipe(shareReplay(1));
    }

    public preorderAreaSearch(searchParameters: SearchParameters, organizationId: string) {
        return this.searcher
            .preorderAreaSearch(searchParameters, organizationId)
            .pipe(
                tap((searchResult) =>
                    this.onSearchResult(FcProductName.PreorderPlatform, searchResult, searchParameters)
                )
            );
    }

    public preorderAreaSearchCache(
        searchParameters: SearchParameters,
        organizationId: string,
        cacheSettings: { ttl: { [key: string]: number } } = { ttl: { hours: 1 } }
    ) {
        if (!this.isRequestRequired(OrderProduct.Preorder, searchParameters, organizationId)) {
            const { value: cachedContent } = this.preorderSearchCache.getValue();
            this.preorderSearchResult.next(cachedContent);
            return;
        }

        if (this.preorderSearchSubscription && !this.preorderSearchSubscription.closed) {
            this.preorderSearchSubscription.unsubscribe();
        }

        this.preorderSearchSubscription = this.searcher
            .preorderAreaSearch(searchParameters, organizationId)
            .subscribe((searchResult) => {
                const { query, filters, pagination } = searchParameters;
                const { articles, count, facets, sortingCriteria } = searchResult;

                const { value: previousResult, ttl } = this.preorderSearchCache.getValue();

                const orderSearchResult = {
                    query,
                    filters: { ...filters, organizationId },
                    result: {
                        articles: previousResult ? [...previousResult.result.articles, ...articles] : articles,
                        count,
                        facets,
                        offset: pagination.offset,
                        sortingCriteria
                    }
                };

                this.preorderSearchCache.next({
                    value: orderSearchResult,
                    ttl: ttl ? ttl : moment().add(cacheSettings.ttl).unix()
                });

                if (orderSearchResult.result.offset === 0) {
                    this.scrollContainerToTop(OrderProduct.Preorder);
                }

                this.preorderSearchResult.next(orderSearchResult);

                this.onSearchResult(FcProductName.PreorderPlatform, searchResult, searchParameters);
                this.trackReorderEvents(orderSearchResult);
            });
    }

    public getPreorderSearchResult() {
        return this.preorderSearchResult.pipe(shareReplay(1));
    }

    public mapUrlParams(paramMap: ParamMap, brandId?: string, isContent?: boolean): SearchParameters {
        const parameters = this.routerHandler.mapUrlParams(paramMap, isContent);
        if (brandId) {
            parameters.filters.brand = brandId;
        }
        return parameters;
    }

    public navigate({ query, pagination, filters, sortingCriteria }: Partial<SearchParameters>, replaceUrl?: boolean) {
        return this.routerHandler.navigate({ query, pagination, filters, sortingCriteria }, replaceUrl);
    }

    public pageResultsLimit(isContent?: boolean) {
        return isContent ? this.routerHandler.itemsPerPageProductImages : this.routerHandler.itemsPerPage;
    }

    public mapFacetToSeasonsFilter(seasonsFacet: Facet[], selectedSeasons: string[]) {
        return this.facetsUiMapper.mapFacetToSeasonsFilter(seasonsFacet, selectedSeasons);
    }

    public mapFacetToSubSeasonsFilter(subSeasonsFacet: Facet[], selectedSubSeasons: string[]) {
        return this.facetsUiMapper.mapFacetToSubSeasonsFilter(subSeasonsFacet, selectedSubSeasons);
    }

    public mapFacetToTargetGroupsFilter(targetGroupsFacet: Facet[], selectedTargetGroups: string[]) {
        return this.facetsUiMapper.mapFacetToTargetGroupsFilter(targetGroupsFacet, selectedTargetGroups);
    }

    public mapFacetToCategoriesFilter(categoriesFacet: Facet[], selectedCategories: string[]) {
        return this.facetsUiMapper.mapFacetToCategoriesFilter(categoriesFacet, selectedCategories);
    }

    public mapFacetToColorsFilter(colorsFacet: Facet[], selectedColors: string[]) {
        return this.facetsUiMapper.mapFacetToColorsFilter(colorsFacet, selectedColors);
    }

    public mapActiveStatusesToFilter(selectedActiveStatuses: string[]) {
        return this.facetsUiMapper.mapActiveStatusesToFilter(selectedActiveStatuses);
    }

    public mapFacetToOrderAreaTargetGroupsFilter(targetGroupsFacet: Facet[], selectedTargetGroups: string[]) {
        return this.facetsUiMapper.mapFacetToOrderAreaTargetGroupsFilter(targetGroupsFacet, selectedTargetGroups);
    }

    public mapFacetToFitsFilter(fitsFacet: Facet[], selectedFits: string[]) {
        return this.facetsUiMapper.mapFacetToFitsFilter(fitsFacet, selectedFits);
    }

    public mapFacetToSizesFilter(sizesFacet: Facet[], selectedSizes: string[]) {
        return this.facetsUiMapper.mapFacetToSizesFilter(sizesFacet, selectedSizes);
    }

    public mapFacetToOrderAreaCatalogsFilter(catalogFacet: FacetWithName[], selectedCatalogs: string[]) {
        return this.facetsUiMapper.mapFacetToCatalogsFilter(catalogFacet, selectedCatalogs);
    }

    public mapFacetToDetailedSizesFilter(sizesFacet: Partial<FacetOptions>, selectedSizes: FilterOptions) {
        return this.facetsUiMapper.mapFacetToDetailedSizesFilter(sizesFacet, selectedSizes);
    }

    public mapFacetToDeliveryDatesFilter(deliveryDatesFacet: Facet[], selectedDeliveryDates: string[]) {
        return this.facetsUiMapper.mapFacetToDeliveryDatesFilter(deliveryDatesFacet, selectedDeliveryDates);
    }

    public mapSearchParamsToTargetGroupLabels(targetGroupSearchParams: string[]) {
        return this.facetsUiMapper.mapSearchParamsToTargetGroupLabels(targetGroupSearchParams);
    }

    public mapSearchParamsToCategoryLabel(categorySearchParam: string) {
        return this.facetsUiMapper.mapSearchParamsToCategoryLabel(categorySearchParam);
    }

    public mapSearchParamsToCategoriesLabel(categoriesSearchParam: string[]) {
        return this.facetsUiMapper.mapSearchParamsToCategoriesLabel(categoriesSearchParam);
    }

    public mapSearchParamsToColorLabels(colorSearchParams: string[]) {
        return this.facetsUiMapper.mapSearchParamsToColorLabels(colorSearchParams);
    }

    public mapSearchParamsToActiveStatusLabels(activeStatusSearchParams: string[]) {
        return this.facetsUiMapper.mapSearchParamsToActiveStatusLabels(activeStatusSearchParams);
    }

    public mapSearchParamsToAwaitingInitialDeliveryLabels(awaitingInitialDelivery: boolean | undefined) {
        return this.facetsUiMapper.mapSearchParamsToAwaitingInitialDeliveryLabels(awaitingInitialDelivery);
    }

    public mapSearchParamsToOATargetGroupLabels(targetGroupSearchParams: string[]) {
        return this.facetsUiMapper.mapSearchParamsToOATargetGroupLabels(targetGroupSearchParams);
    }

    public mapSearchParamsToSeasonLabels(seasonSearchParams: string[]) {
        return this.facetsUiMapper.mapSearchParamsToSeasonLabels(seasonSearchParams);
    }

    public mapSearchParamsToSizeLabels(sizeSearchParams: string[]) {
        return this.facetsUiMapper.mapSearchParamsToSizeLabels(sizeSearchParams);
    }

    public mapSearchParamsToFitLabels(fitSearchParams: string[]) {
        return this.facetsUiMapper.mapSearchParamsToFitLabels(fitSearchParams);
    }

    public getStaticColorsFilter(selectedColors: string[]) {
        return this.filtersProvider.getColorsFilter(selectedColors);
    }

    public getStaticPeriodsFilter(selectedPeriod: string) {
        return this.filtersProvider.getPeriodsFilter(selectedPeriod);
    }

    public getStaticTargetGroupsFilter(selectedTargetGroups: string[]) {
        return this.filtersProvider.getTargetGroupsFilter(selectedTargetGroups);
    }

    public getStaticSeasonsFilter(selectedSeasons: string[]) {
        return this.filtersProvider.getSeasonsFilter(selectedSeasons);
    }

    public getStaticMinimumStockFilter() {
        return this.filtersProvider.getMinimumStockFilter();
    }

    public getStaticMinimumDiscountFilter() {
        return this.filtersProvider.getMinimumDiscountFilter();
    }

    public getStaticCategoriesFilter(selectedCategories: string[]) {
        return this.filtersProvider.getCategoriesFilter(selectedCategories);
    }

    public getBrandFilter(brands: { _id: string; name: string }[], selectedBrand: string) {
        return this.filtersProvider.getBrandFilter(brands, selectedBrand);
    }

    public getSelectedCategoryFilters(categoryFilters: ListItem[]) {
        return this.filtersProvider.getSelectedCategoryFilters(categoryFilters);
    }

    public getSortingCriteria(
        sortingCriteria,
        sortingCriteriaValues: { sortBy: string; sortOrder: string }
    ): SortingCriteria {
        return this.searcher.getSortingCriteria(sortingCriteria, sortingCriteriaValues);
    }

    public hasDynamicCategory(selectedBrand: string) {
        return this.filtersProvider.hasDynamicCategory(selectedBrand);
    }

    private scrollContainerToTop(type: OrderProduct) {
        const containerElement = document.getElementById(
            type === OrderProduct.Preorder ? 'fc-preorder-content-page-container' : 'fc-content-page-container'
        );

        if (containerElement) {
            containerElement.scrollTo({ top: 0, left: 0 });
        }
    }

    private isRequestRequired(type: OrderProduct, searchParameters: SearchParameters, organizationId: string): boolean {
        const isPreorder = type === OrderProduct.Preorder;
        const cache = isPreorder ? this.preorderSearchCache : this.searchCache;
        const { value: cachedContent } = cache.getValue();
        const { ttl: existingTTL } = cache.getValue();

        const hasCacheExpired = existingTTL && moment.unix(existingTTL).isBefore();

        if (hasCacheExpired) {
            searchParameters.pagination.offset = 0;
            cache.next({ value: null, ttl: null });
            return true;
        }

        if (cachedContent) {
            const hasNewFiltersApplied = !_.isEqual(
                isPreorder ? { ...searchParameters.filters, organizationId } : searchParameters.filters,
                cachedContent.filters
            );

            if (hasNewFiltersApplied) {
                cache.next({ value: null, ttl: null });
                return true;
            }

            if (this.latestRouterEvent) {
                const isBackButtonPressed = this.latestRouterEvent.navigationTrigger === 'popstate';
                const isTryingToFetchCachedContent = searchParameters.pagination.offset <= cachedContent.result.offset;
                const isComingBackFromPDP = isTryingToFetchCachedContent && isBackButtonPressed;
                const isSameRoute = this.router.isActive(this.latestRouterEvent.url, this.routerMatchConfig);
                const isRequestingFirstPage = searchParameters.pagination.offset === 0;

                if (isComingBackFromPDP && !hasNewFiltersApplied) {
                    return false;
                } else if (!isSameRoute || isRequestingFirstPage) {
                    cache.next({ value: null, ttl: null });
                    return true;
                }
            }
        }
        return true;
    }

    private onSearchResult(
        fcProduct: FcProductName,
        searchResult: { count: number },
        { query, filters, sortingCriteria }: SearchParameters
    ) {
        this.segmentService.track('Platform search result', {
            fcProductName: fcProduct,
            searchQuery: query,
            resultCount: searchResult.count,
            filters: {
                brands: _.get(filters, 'brands', null),
                brand: _.get(filters, 'brand', null),
                categories: _.get(filters, 'categories', null),
                seasons: _.get(filters, 'seasons', null),
                targetGroups: _.get(filters, 'targetGroups', null),
                sizes: _.get(filters, 'sizes', null),
                generalSizes: _.get(filters, 'generalSizes', null),
                widths: _.get(filters, 'widths', null),
                lengths: _.get(filters, 'lengths', null),
                colors: _.get(filters, 'colors', null),
                minimumDiscount: _.get(filters, 'minimumDiscount', null),
                minimumStock: _.get(filters, 'minimumStock', null),
                mainCategory: _.get(filters, 'mainCategory', null),
                level1Category: _.get(filters, 'level1Category', null),
                orderedArticles: _.get(filters, 'orderedArticles', null)
            },
            sortingCriteria:
                sortingCriteria?.sortBy && `${sortingCriteria.sortBy} ${_.get(sortingCriteria, 'sortOrder', '')}`
        });
    }

    private trackReorderEvents(searchAndResult) {
        if (searchAndResult?.query) {
            this.segmentService.trackGtm(TrackingEvents.SEARCH, {
                page_type: 'reorder',
                search_results: searchAndResult?.result?.count,
                search_term: searchAndResult?.query
            });
        }
        const articles = searchAndResult?.result?.articles;
        const trackItems = _.map(articles, (article) => ({
            item_id: article.styleGroupId,
            item_name: article.name,
            affiliation: 'reorder',
            currency: article.price?.currency,
            discount: article.price?.discount,
            item_brand: article.brandName,
            item_list_id: 'related_products',
            item_list_name: 'Related Products',
            item_variant: article.colorGroupId,
            price: article.price?.discountedPurchasePrice || article.price?.purchasePrice
        }));

        this.segmentService.track(TrackingEvents.VIEW_ITEM_LIST, {
            ecommerce: {
                item_list_id: 'related_products',
                item_list_name: 'Related products',
                items: trackItems
            }
        });
    }

    private cleanUp() {
        this.reorderSearchResult = new ReplaySubject(1);
        this.preorderSearchResult = new ReplaySubject(1);
        this.preorderSearchCache.next({ value: null, ttl: null });
        this.searchCache.next({ value: null, ttl: null });
    }
}

export interface ReorderSearch {
    query: string;
    filters: FilterOptions;
    result: {
        articles: ArticleForOrder[];
        facets: FacetOptions;
        count: number;
        offset: number;
        sortingCriteria?: Sorting;
    };
}
